<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import ForwardMessageModal from './ForwardMessageModal.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ConversationAiAgentAPI from 'dashboard/api/conversationAiAgent';
import Editor from 'dashboard/components-next/Editor/Editor.vue';

export default {
  components: {
    AddCannedModal,
    ForwardMessageModal,
    MenuItem,
    ContextMenu,
    NextButton,
    Editor,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      isForwardModalOpen: false,
      showEditModal: false,
      editingContent: '',
      isSavingEdit: false,
      showReportModal: false,
      reportReason: '',
      isSavingReport: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
      currentChat: 'getSelectedChat',
    }),
    // Canal/medium da inbox: define quais marcas de formatacao o editor
    // rich text oferece (ex: WhatsApp usa *negrito*, _italico_, ~tachado~).
    inbox() {
      const inboxId = this.currentChat?.inbox_id;
      return inboxId ? this.$store.getters['inboxes/getInbox'](inboxId) : {};
    },
    channelType() {
      return this.inbox?.channel_type || '';
    },
    medium() {
      return this.inbox?.medium || '';
    },
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
    showForwardModal() {
      this.handleClose();
      this.isForwardModalOpen = true;
    },
    hideForwardModal() {
      this.isForwardModalOpen = false;
    },
    openEditModal() {
      this.handleClose();
      this.editingContent = this.messageContent || '';
      this.showEditModal = true;
    },
    closeEditModal() {
      this.showEditModal = false;
      this.editingContent = '';
      this.isSavingEdit = false;
    },
    async confirmEdit() {
      const trimmed = this.editingContent.trim();
      if (!trimmed) {
        useAlert('Conteudo nao pode estar vazio');
        return;
      }
      if (trimmed === this.messageContent) {
        this.closeEditModal();
        return;
      }
      this.isSavingEdit = true;
      try {
        await this.$store.dispatch('editMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
          content: trimmed,
        });
        useAlert('Mensagem editada — propagada pro WhatsApp');
        this.closeEditModal();
      } catch (error) {
        const apiError =
          error?.response?.data?.error ||
          error?.message ||
          'Falha ao editar mensagem';
        useAlert(apiError);
        this.isSavingEdit = false;
      }
    },
    openReportModal() {
      this.handleClose();
      this.reportReason = '';
      this.showReportModal = true;
    },
    closeReportModal() {
      this.showReportModal = false;
      this.reportReason = '';
      this.isSavingReport = false;
    },
    async confirmReport() {
      const trimmed = this.reportReason.trim();
      if (!trimmed) {
        useAlert('Descreva o erro antes de enviar.');
        return;
      }
      this.isSavingReport = true;
      try {
        await ConversationAiAgentAPI.reportFeedback(this.conversationId, {
          reason: trimmed,
          message_id: this.messageId,
        });
        useAlert('Report enviado — a IA vai aprender com isso. Obrigado!');
        this.closeReportModal();
      } catch (error) {
        useAlert('Nao foi possivel enviar o report.');
        this.isSavingReport = false;
      }
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Forward Message Modal -->
    <ForwardMessageModal
      v-if="isForwardModalOpen && enabledOptions['forward']"
      :message="message"
      :on-close="hideForwardModal"
    />
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-ri-more-2-line"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen && !isForwardModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <MenuItem
          v-if="enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="enabledOptions['forward']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.FORWARD'),
          }"
          variant="icon"
          @click.stop="showForwardModal"
        />
        <MenuItem
          v-if="enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr />
        <MenuItem
          v-if="enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="enabledOptions['edit'] || enabledOptions['delete']" />
        <MenuItem
          v-if="enabledOptions['edit']"
          :option="{
            icon: 'edit',
            label: 'Editar mensagem',
          }"
          variant="icon"
          @click.stop="openEditModal"
        />
        <MenuItem
          v-if="enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
        <hr v-if="enabledOptions['report']" />
        <MenuItem
          v-if="enabledOptions['report']"
          :option="{
            icon: 'warning',
            label: 'Reportar erro da IA',
          }"
          variant="icon"
          @click.stop="openReportModal"
        />
      </div>
    </ContextMenu>

    <!-- Modal de edicao -->
    <woot-modal
      v-if="showEditModal"
      v-model:show="showEditModal"
      :on-close="closeEditModal"
      size="medium"
    >
      <div class="p-6">
        <h3 class="text-base font-semibold mb-1 text-n-slate-12">Editar mensagem</h3>
        <p class="text-xs text-n-slate-11 mb-4">
          A edicao sera propagada pro WhatsApp do contato. WhatsApp permite editar so dentro de 15 minutos do envio.
        </p>
        <Editor
          v-model="editingContent"
          :editor-key="`edit-msg-${messageId}`"
          placeholder="Edite a mensagem..."
          :focus-on-mount="true"
          :enable-canned-responses="false"
          :enable-variables="false"
          :show-character-count="false"
          :channel-type="channelType"
          :medium="medium"
          :disabled="isSavingEdit"
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3"
            :disabled="isSavingEdit"
            @click="closeEditModal"
          >Cancelar</button>
          <button
            class="px-4 py-2 rounded-md bg-n-brand text-white text-sm hover:bg-n-brand/90 disabled:opacity-50"
            :disabled="isSavingEdit"
            @click="confirmEdit"
          >{{ isSavingEdit ? 'Salvando...' : 'Salvar e propagar' }}</button>
        </div>
      </div>
    </woot-modal>

    <!-- Modal de report de erro da IA (SiteUp) -->
    <woot-modal
      v-if="showReportModal"
      v-model:show="showReportModal"
      :on-close="closeReportModal"
      size="medium"
    >
      <div class="p-6">
        <h3 class="text-base font-semibold mb-1 text-n-slate-12">Reportar erro da IA</h3>
        <p class="text-xs text-n-slate-11 mb-4">
          Descreva o que a IA errou nesta resposta. Isso vira uma correcao que entra
          no prompt do agente pra ele nao repetir o erro.
        </p>
        <textarea
          v-model="reportReason"
          rows="4"
          placeholder="Ex: prometeu desconto sem aprovacao; usou nome errado do cliente..."
          class="w-full px-3 py-2 rounded-md border border-n-slate-4 bg-n-background text-n-slate-12 text-sm"
          :disabled="isSavingReport"
        />
        <div class="flex justify-end gap-2 mt-4">
          <button
            class="px-4 py-2 rounded-md border border-n-slate-4 text-sm text-n-slate-12 hover:bg-n-slate-3"
            :disabled="isSavingReport"
            @click="closeReportModal"
          >Cancelar</button>
          <button
            class="px-4 py-2 rounded-md bg-n-brand text-white text-sm hover:bg-n-brand/90 disabled:opacity-50"
            :disabled="isSavingReport"
            @click="confirmReport"
          >{{ isSavingReport ? 'Enviando...' : 'Enviar report' }}</button>
        </div>
      </div>
    </woot-modal>
  </div>
</template>

<style lang="scss" scoped>
.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  :deep(.modal-container) {
    @apply max-w-[30rem];

    h2 {
      @apply font-medium text-base;
    }
  }
}
</style>
